import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'

import useSnackbar from '../Snackbar/useSnackbar'

const useAssignmentSession = ({ isBlind = false } = {}) => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    error: null,
  })

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const startAssignment = useCallback(
    async (sessionId) => {
      setStartUpdating()

      try {
        const endpoint = isBlind ? 'assignment-blind/start' : 'assignment/start'
        const result = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/${endpoint}`
        )

        setSuccessFetching(result.data.body)
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
      isBlind,
    ]
  )

  const confirmAssignment = useCallback(
    async (sessionId, availabilityAt) => {
      setStartUpdating()

      try {
        const endpoint = isBlind
          ? 'confirm-assignment-blind'
          : 'confirm-assignment'
        const payload = isBlind ? {} : availabilityAt ? { availabilityAt } : {}
        const result = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/${endpoint}`,
          payload
        )

        setSuccessFetching(result.data.body)
        return true
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message,
        })

        return false
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
      isBlind,
    ]
  )

  return {
    // isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    result: state.result,
    assignmentEndedAt: state.assignmentEndedAt,
    blindAssignmentEndedAt: state.blindAssignmentEndedAt,
    startAssignment,
    confirmAssignment,
  }
}

export default useAssignmentSession
